import { confirm } from "@bps/fluent-ui";
import { Country } from "@libs/enums/country.enum.ts";
import {
  OrgUnitHierarchyType,
  Permission
} from "@libs/gateways/core/CoreGateway.dtos.ts";
import {
  AddressType,
  CommunicationDto,
  CommunicationType,
  LocationDataDto,
  NzOrgUnitIdentifierDto,
  OrgUnitCompanyDataType
} from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { routes } from "@libs/routing/routes.ts";
import { CoreStore } from "@stores/core/CoreStore.ts";
import { Address } from "@stores/core/models/Address.ts";
import { formatPhone, parsePhone } from "@stores/core/models/Communication.ts";
import { OrgUnit } from "@stores/core/models/OrgUnit.ts";
import { PracOrgUnit } from "@stores/practice/models/PracOrgUnit.ts";
import { RootStore } from "@stores/root/RootStore.ts";

import { LocationDetailsValues } from "../location-details/LocationDetails.types.ts";

const createBhbAppointmentTypes = async (
  root: RootStore,
  locationId: string
) => {
  // BestHealth.Booking creates empty Location Appointment Types so we need to update them to match the existing appointment types
  const [appointmentTypes = []] = await Promise.all([
    root.bhb.getAppointmentTypes(),
    root.bhb.getAppointmentTypesForLocation(locationId) // preload new appointment types so the update method has access to their eTags
  ]);

  await Promise.resolve([
    ...appointmentTypes?.map(appointmentType =>
      root.bhb.putAppointmentTypeForLocation(
        appointmentType.dto,
        locationId ?? ""
      )
    )
  ]);
};

export const getLocationFormInitialValues = (
  options: {
    orgUnit: OrgUnit;
    pracOrgUnit: PracOrgUnit | undefined;
    locationOrgUnit: OrgUnit | undefined;
  },
  core: CoreStore
): LocationDetailsValues => {
  const { orgUnit, pracOrgUnit, locationOrgUnit } = options;

  const country = core.tenantDetails && core.tenantDetails.country;

  const address = locationOrgUnit?.firstAddress || orgUnit?.firstAddress;

  const hasMultiLocationPermission = core.hasPermissions([
    Permission.PreReleaseMultiLocationAllowed
  ]);

  const workPhone: CommunicationDto = {
    type: CommunicationType.WorkPhone,
    value: locationOrgUnit
      ? locationOrgUnit.workPhone || ""
      : orgUnit?.workPhone || "",
    preferred: false
  };

  if (hasMultiLocationPermission) {
    const address = locationOrgUnit?.firstAddress;

    const workPhone: CommunicationDto = {
      type: CommunicationType.WorkPhone,
      value:
        locationOrgUnit && locationOrgUnit.workPhone
          ? locationOrgUnit.workPhone
          : "",
      preferred: false
    };

    const practiceName = locationOrgUnit ? locationOrgUnit?.name : "";

    return {
      id: pracOrgUnit?.id,
      practiceName: locationOrgUnit ? locationOrgUnit?.name : "",
      isInactiveLocation: locationOrgUnit?.isInactive,
      appointmentBookMarker:
        pracOrgUnit?.orgUnitLocationData?.appointmentBookMarkerCode,
      address1: address?.street1,
      address2: address?.street2,
      suburb: address?.suburb,
      country: address?.country ?? country,
      postCode: address?.postCode,
      city: address?.city,
      state: address?.state,
      phone: formatPhone(workPhone.value, address?.country),
      timezone: locationOrgUnit?.timezone,
      email: locationOrgUnit?.email,
      hpiFacilityId: pracOrgUnit?.nzOrgUnitIdentifier?.hpiFacilityId,
      defaultLocation: pracOrgUnit?.orgUnitLocationData?.defaultLocation,
      smsName:
        pracOrgUnit?.dto.smsName === practiceName
          ? ""
          : pracOrgUnit?.dto.smsName
    };
  }

  return {
    id: pracOrgUnit?.id || locationOrgUnit?.id || orgUnit?.id,
    practiceName: locationOrgUnit?.name || orgUnit?.name || "",
    isInactiveLocation: locationOrgUnit?.isInactive || orgUnit?.isInactive,
    appointmentBookMarker:
      pracOrgUnit?.orgUnitLocationData?.appointmentBookMarkerCode || "",
    address1: address?.street1,
    address2: address?.street2,
    suburb: address?.suburb,
    country: address?.country || country,
    postCode: address?.postCode,
    city: address?.city,
    state: address?.state,
    phone: formatPhone(workPhone.value, country),
    timezone: locationOrgUnit?.timezone || orgUnit?.timezone,
    email: locationOrgUnit?.email || orgUnit?.email,
    hpiFacilityId: pracOrgUnit?.nzOrgUnitIdentifier?.hpiFacilityId,
    defaultLocation: pracOrgUnit?.orgUnitLocationData?.defaultLocation,
    smsName: pracOrgUnit?.dto.smsName
  };
};

export const onLocationFormSubmit = async (
  values: LocationDetailsValues,
  root: RootStore,
  options: {
    initialValues: LocationDetailsValues;
    parentOrgUnitId: string | undefined;
  }
) => {
  const { core, practice, notification, routing } = root;

  const { initialValues, parentOrgUnitId } = options;

  try {
    // Handle default location confirmation if needed
    if (!initialValues.defaultLocation && values.defaultLocation) {
      const isConfirmed = await confirm({
        cancelButtonProps: {
          text: "Cancel"
        },
        confirmButtonProps: {
          text: "Confirm"
        },
        dialogContentProps: {
          title: "Confirm change in default location",
          subText: `Do you want to set ${values.practiceName} as the default location?`
        }
      });

      if (!isConfirmed) {
        return;
      }

      notification.success(
        `${values.practiceName} set as the default location`
      );
    }

    const { phone, email, hpiFacilityId } = values;
    const workPhone = parsePhone(phone);
    const communications: CommunicationDto[] = [];

    // Handle communications
    if (email) {
      communications.push({
        type: CommunicationType.Email,
        value: email,
        preferred: false
      });
    }

    if (workPhone) {
      communications.push({
        type: CommunicationType.WorkPhone,
        value: workPhone,
        preferred: false
      });
    }

    // Handle address
    let city: string | undefined;
    if (values.city) {
      if (values.country === Country.Australia) {
        city = "";
      } else {
        city = values.city;
      }
    }

    let street2: string | undefined;
    if (values.address2) {
      street2 = values.address2;
    } else if (initialValues.address2) {
      street2 = "";
    }

    const addresses: Address[] = [
      {
        type: AddressType.Physical,
        street1: values.address1 || "",
        street2,
        suburb: values.suburb,
        city,
        state: values.state,
        postCode: values.postCode,
        country: values.country || Country.NewZealand
      }
    ];

    // Location data for colors and nickname
    const locationData: LocationDataDto = {
      orgUnitCompanyDataTypeCode: OrgUnitCompanyDataType.LocationData,
      appointmentBookMarkerCode: values.appointmentBookMarker || "",
      defaultLocation: values.defaultLocation
    };

    // HPI Facility ID for NZ
    const nzOrgUnitData: NzOrgUnitIdentifierDto = {
      hpiFacilityId,
      orgUnitCompanyDataTypeCode: OrgUnitCompanyDataType.NzOrgUnitIdentifier
    };

    let locationId: string | undefined;

    // Update existing or create new location
    if (values.id) {
      await core.updateOrgUnit({
        id: values.id,
        addresses,
        parentOrgUnitId,
        contact: communications,
        name: values.practiceName,
        hierarchyType: OrgUnitHierarchyType.Location,
        timeZone: values.timezone
      });
      locationId = values.id;
      notification.success(`${values.practiceName} has been updated`);
    } else {
      const response = await core.addOrgUnit({
        parentOrgUnitId,
        name: values.practiceName,
        timeZone: values.timezone,
        contact: communications,
        addresses,
        hierarchyType: OrgUnitHierarchyType.Location
      });
      locationId = response.id;
      notification.success(`${values.practiceName} has been added`);

      // Create appointment types for new locations
      await createBhbAppointmentTypes(root, locationId);
    }

    // Update practice data
    await practice.updateOrgUnit({
      id: locationId,
      orgUnitCompanyData: [locationData, nzOrgUnitData],
      smsName: values.smsName || values.practiceName
    });

    if (!values.id) {
      // reload list of location data
      root.practice.getAllOrgUnitsLocationData({ ignoreCache: true });
    }

    // Navigate back to practice settings
    routing.push(routes.settings.practices.basePath.path({}));
  } catch {
    notification.error("Failed to save location details");
  }
};
